import { useEffect, useRef } from 'react';
import { rememberSiteSelectVariant, track } from './analytics';
import type { SiteSelectVariant } from './siteSelectVariant';

/**
 * What customers do when choosing where to order from, the same events whichever way it is given (a page, or a modal over the menu), with
 * the `site_select_variant` to tell them apart: see docs/analytics.md. They go to Orderswift's own property only, as it is Orderswift's
 * comparison to make.
 */

type Params = Record<string, unknown>;

// The page's tabs are links to other routes, which may draw the choice again: that's not another view of it
let tabClickedAt = 0;

const send = (name: string, variant: SiteSelectVariant, params: Params = {}) =>
    track(name, { site_select_variant: variant, ...params }, { to: 'orderswift' });

/** The choice is on screen: once, however many times it draws. `ready` is for when there is something to choose from. */
export const useSiteSelectView = (variant: SiteSelectVariant, ready: boolean, params: Params) => {
    const sent = useRef(false);

    useEffect(() => {
        if (!ready || sent.current) return;

        sent.current = true;
        rememberSiteSelectVariant(variant);

        if (Date.now() - tabClickedAt < 3000) return;

        send('site_select_view', variant, params);
    }, [ready]);
};

/** A different channel (collection, delivery...) is looked at. */
export const trackSiteSelectTab = (variant: SiteSelectVariant, channel: string) => {
    tabClickedAt = Date.now();
    send('site_select_tab', variant, { channel });
};

/** Looking for somewhere by typing ('text') or by asking for the nearest ('location'). */
export const trackSiteSelectSearch = (variant: SiteSelectVariant, channel: string, method: 'text' | 'location') =>
    send('site_select_search', variant, { channel, method });

/** Somewhere is picked from the list, before going on (the modal has this step; the page's cards go on at once). */
export const trackSiteSelectChoose = (variant: SiteSelectVariant, channel: string, siteHandle: string) =>
    send('site_select_choose', variant, { channel, site_handle: siteHandle });

/** The choice is made, and on they go to the menu: the end of the step, in either. */
export const trackSiteSelectComplete = (variant: SiteSelectVariant, channel: string, siteHandle?: string) =>
    send('site_select_complete', variant, { channel, site_handle: siteHandle });
